import { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Upload, Loader2, FileText, Trash2, CheckCircle2, XCircle, Link as LinkIcon,
  Download, Paperclip, AlertCircle, Search, Eye, Wallet, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ExportCamtBtn from "@/components/compta/ExportCamtBtn";
import CompteSelect from "@/components/CompteSelect";

function fmtChf(n) {
  return (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
}

const STATUT_PROV_COLORS = {
  rapproche_auto: "bg-emerald-50 text-emerald-700 border-emerald-200",
  en_attente: "bg-amber-50 text-amber-700 border-amber-200",
  ignore: "bg-gray-100 text-gray-500 border-gray-200",
  validee: "bg-blue-50 text-blue-700 border-blue-200",
};

export default function ImportBancaireTab() {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedImport, setSelectedImport] = useState(null);
  const [focusProvisoireId, setFocusProvisoireId] = useState(null); // iter105 — scroll/highlight

  const fetchImports = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal/camt/imports");
      setImports(data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchImports(); }, [fetchImports]);

  // iter105 — Lecture des query params (deep link depuis l'audit modal)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const iid = params.get("import_id");
    const pid = params.get("focus_provisoire");
    if (iid) {
      setSelectedImport(iid);
      if (pid) setFocusProvisoireId(pid);
    }
  }, []);

  if (selectedImport) {
    return <ImportDetail iid={selectedImport} focusProvisoireId={focusProvisoireId} onBack={() => { setSelectedImport(null); setFocusProvisoireId(null); fetchImports(); }} />;
  }

  return (
    <div data-testid="import-bancaire-tab" className="space-y-4">
      <Card className="p-4 border-gray-200 bg-gradient-to-br from-[#52a675]/5 to-transparent flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900">Import CAMT (ISO 20022)</div>
          <div className="text-xs text-gray-600 mt-0.5">Téléversez vos relevés bancaires CAMT.053 ou CAMT.054 (.xml). Le système détecte automatiquement les doublons et propose le rapprochement avec les factures en attente.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* iter340 — export des mouvements avec leur rapprochement */}
          <ExportCamtBtn />
          <Button onClick={() => setUploadDialog(true)} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="camt-upload-btn">
            <Upload className="w-4 h-4 mr-2" /> Importer un fichier CAMT
          </Button>
        </div>
      </Card>

      {loading && <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
      {!loading && imports.length === 0 && (
        <Card className="p-8 text-center text-gray-400 border-gray-200 border-dashed">Aucun import. Cliquez sur "Importer un fichier CAMT" pour commencer.</Card>
      )}

      {!loading && imports.length > 0 && (
        <Card className="border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Fichier</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Compte</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Période</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Mvts</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Validées</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {imports.map((imp) => (
                <tr key={imp.id} className="border-t border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => setSelectedImport(imp.id)} data-testid={`import-row-${imp.id}`}>
                  <td className="px-3 py-2 text-xs text-gray-600">{new Date(imp.created_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="px-3 py-2 text-xs font-mono">{imp.fichier_nom}</td>
                  <td className="px-3 py-2"><span className="font-mono text-xs">{imp.compte_bancaire_numero}</span> · {imp.compte_bancaire_libelle}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{fmtDate(imp.date_du)} → {fmtDate(imp.date_au)}</td>
                  <td className="px-3 py-2 text-right font-mono text-sm">{imp.nb_provisoires_crees} <span className="text-xs text-gray-400">({imp.nb_doublons_ignores} doublons)</span></td>
                  <td className="px-3 py-2 text-right font-mono text-sm">{imp.nb_valides} / {imp.nb_provisoires_crees}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={imp.statut === "termine" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}>{imp.statut === "termine" ? "Terminé" : "En cours"}</Badge></td>
                  <td className="px-3 py-2 text-right text-xs text-gray-400">→</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <UploadCamtDialog open={uploadDialog} onClose={() => setUploadDialog(false)} onUploaded={(id) => { setUploadDialog(false); fetchImports(); setSelectedImport(id); }} />
    </div>
  );
}

function UploadCamtDialog({ open, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [compte, setCompte] = useState("1020");
  const [comptes, setComptes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [forceImport, setForceImport] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (open) {
      setLastResult(null);
      setForceImport(false);
      api.get("/journal/plan-comptes").then(({ data }) => {
        setComptes(data.filter((c) => /^10(2|3)/.test(c.numero)));  // comptes 102x/103x = banque
      });
    }
  }, [open]);

  const submit = async () => {
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("fichier", file);
      fd.append("compte_bancaire_numero", compte);
      fd.append("force_import", forceImport ? "true" : "false");
      const { data } = await api.post("/journal/camt/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLastResult(data);
      const msg = `${data.nb_provisoires_crees} mouvement(s) importé(s) · ${data.nb_rapproches_auto} rapproché(s) automatiquement · ${data.nb_doublons_ignores} doublon(s) ${forceImport ? "détecté(s) (importé(s) malgré tout)" : "ignoré(s)"}`;
      if (data.nb_doublons_ignores > 0 && !forceImport) {
        toast.warning(msg, { duration: 6000 });
      } else {
        toast.success(msg);
      }
      // Si rien n'a été importé et que tout était doublon, on reste sur le dialog pour montrer le détail
      if (data.nb_provisoires_crees > 0) {
        setTimeout(() => onUploaded(data.id), 1500);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Importer un relevé bancaire CAMT</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Fichier .xml (CAMT.053 ou 054) *</Label>
            <Input type="file" accept=".xml" onChange={(e) => setFile(e.target.files[0])} data-testid="camt-file-input" />
            {file && <div className="text-xs text-gray-600 mt-1">📄 {file.name} ({(file.size / 1024).toFixed(1)} Ko)</div>}
          </div>
          <div>
            <Label>Compte bancaire (plan comptable) *</Label>
            <CompteSelect
              value={compte}
              onValueChange={setCompte}
              options={comptes.map((c) => ({ value: c.numero, label: `${c.numero} – ${c.libelle}` }))}
              placeholder="Choisir un compte"
              data-testid="camt-compte-select"
            />
          </div>
          <label className="flex items-start gap-2 p-2 border border-amber-200 bg-amber-50 rounded cursor-pointer" data-testid="camt-force-import-label">
            <input
              type="checkbox"
              checked={forceImport}
              onChange={(e) => setForceImport(e.target.checked)}
              className="mt-0.5"
              data-testid="camt-force-import-checkbox"
            />
            <div className="text-xs">
              <div className="font-semibold text-amber-900">Forcer l'import des doublons</div>
              <div className="text-amber-700">À utiliser uniquement si vous savez qu'une transaction identique a été importée par erreur et doit être ré-injectée. Sinon, laissez décoché : les transactions strictement identiques (même AcctSvcrRef bancaire) sont automatiquement ignorées.</div>
            </div>
          </label>
          <p className="text-[11px] text-gray-500">
            Détection de doublons stricte : seules les transactions avec le même identifiant bancaire (AcctSvcrRef)
            ou identiques trait-pour-trait (date, montant, sens, IBAN contrepartie, libellé, référence) sont considérées comme doublons.
          </p>

          {lastResult && lastResult.doublons_details && lastResult.doublons_details.length > 0 && (
            <div className="border border-amber-200 bg-amber-50/50 rounded p-2 max-h-48 overflow-y-auto">
              <div className="text-xs font-semibold text-amber-900 mb-1">
                {lastResult.doublons_details.length} doublon(s) détecté(s) {lastResult.force_import ? "(importé(s) malgré tout)" : "(ignoré(s))"} :
              </div>
              <ul className="space-y-1">
                {lastResult.doublons_details.map((d, i) => (
                  <li key={i} className="text-[11px] text-amber-800 font-mono flex gap-2" data-testid={`doublon-detail-${i}`}>
                    <span className="text-amber-600">{fmtDate(d.date_valeur)}</span>
                    <span className={d.sens === "CRDT" ? "text-emerald-700" : "text-red-700"}>{d.sens === "CRDT" ? "+" : "-"}{fmtChf(d.montant)}</span>
                    <span className="flex-1 truncate text-gray-700">{d.contrepartie_nom || d.libelle}</span>
                    <span className="text-amber-600 italic">{d.motif}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button onClick={submit} disabled={submitting || !file} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="camt-upload-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Importer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportDetail({ iid, focusProvisoireId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [iaLoading, setIaLoading] = useState(false);

  // iter106 — Audit en ligne : on charge l'audit en parallèle pour décorer chaque
  // ligne avec ses anomalies (badges inline). Map { provisoire_id → [messages] }.
  const [auditByPid, setAuditByPid] = useState(new Map());
  // iter221 — Préserve le scroll autour de chaque re-fetch (rapprochement,
  // validation, dévalidation…). Sans ça, `setLoading(true)` remplaçait le
  // contenu par un spinner → scroll retombait en haut de la page.
  const initialLoadDoneRef = useRef(false);

  const fetchData = useCallback(async () => {
    // Loader visible uniquement au 1er chargement — les re-fetch après action
    // se font en silence pour ne pas déclencher un re-render qui reset le scroll.
    if (!initialLoadDoneRef.current) setLoading(true);
    // Snapshot scroll avant re-render
    const scrollY = window.scrollY;
    try {
      const [{ data: d }, { data: pc }, auditRes] = await Promise.all([
        api.get(`/journal/camt/imports/${iid}`),
        api.get("/journal/plan-comptes"),
        api.get(`/journal/camt/audit`, { params: { import_id: iid } }).catch(() => ({ data: { anomalies: {} } })),
      ]);
      setData(d);
      setPlan(pc);

      // Construit la map provisoire_id → anomalies inline
      const map = new Map();
      const an = auditRes.data.anomalies || {};
      const push = (pid, msg, severity) => {
        if (!pid) return;
        if (!map.has(pid)) map.set(pid, []);
        map.get(pid).push({ msg, severity });
      };
      (an.incoherences_alloue_superieur_montant || []).forEach((r) =>
        push(r.id, `Sur-alloué : ${(r.alloue || 0).toFixed(2)} CHF répartis pour ${(r.montant || 0).toFixed(2)} CHF reçus (+${(r.excedent || 0).toFixed(2)} CHF en trop)`, "critique"),
      );
      (an.ecarts_validation_sans_compte || []).forEach((r) =>
        push(r.id, `Écart ${(r.ecart || 0).toFixed(2)} CHF validé sans compte d'imputation`, "attention"),
      );
      (an.doublons_dedup_key || []).forEach((r) => {
        (r.ids || []).forEach((pid) =>
          push(pid, `Doublon probable (même date+montant+ref) — ${r.ids.length} occurrences`, "important"),
        );
      });
      (an.doublons_acct_servicer_ref || []).forEach((r) => {
        (r.ids || []).forEach((pid) =>
          push(pid, `Doublon de référence bancaire — ${r.ids.length} occurrences importées`, "critique"),
        );
      });
      (an.provisoires_orphelins_rapproche_facture_non_valides || []).forEach((r) =>
        push(r.id, "Rapprochement oublié — il manque la validation finale", "attention"),
      );
      setAuditByPid(map);
    } finally {
      setLoading(false);
      initialLoadDoneRef.current = true;
      // Restaure le scroll juste après le rendu React (2 rAF pour laisser
      // le temps aux enfants de se re-monter avant de scroller).
      if (scrollY > 0) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: "auto" }));
        });
      }
    }
  }, [iid]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // iter105 — Scroll vers + highlight de la ligne ciblée (deep link audit)
  useEffect(() => {
    if (!focusProvisoireId || !data) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="prov-${focusProvisoireId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-amber-400", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2"), 4000);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [focusProvisoireId, data]);

  const relancerIA = async () => {
    setIaLoading(true);
    try {
      const { data: r } = await api.post(`/journal/camt/imports/${iid}/suggerer-ia`);
      toast.success(`✨ IA : ${r.suggestions} suggestion(s) sur ${r.total} ligne(s) en attente`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur IA");
    } finally { setIaLoading(false); }
  };

  const updateProv = async (pid, patch) => {
    try {
      await api.patch(`/journal/provisoires/${pid}`, patch);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // iter86-bvr — Actions de rapprochement bancaire QR/BVR
  const confirmerFacture = async (pid) => {
    try {
      const { data } = await api.patch(`/journal/provisoires/${pid}/confirmer-facture`);
      const msg = data.was_fully_paid
        ? `Facture ${data.facture_numero} marquée payée (encaissement CHF ${data.total_encaisse})`
        : `Encaissement partiel ajouté à ${data.facture_numero} — reste CHF ${data.reste_a_payer}`;
      toast.success(msg);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const rejeterFacture = async (pid) => {
    try {
      await api.patch(`/journal/provisoires/${pid}/rejeter-facture`);
      toast.success("Suggestion rejetée");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const [linkModal, setLinkModal] = useState(null);  // {pid, montant} or null
  // iter341 — Modal de rapprochement manuel aux factures fournisseurs (DBIT)
  const [ffModal, setFfModal] = useState(null);  // {pid, montant, suggere}
  // iter100h-P2 — Modal pour assigner un compte d'écart aux provisoires refusés
  const [ecartModal, setEcartModal] = useState(null);  // {refuses: [{id, raison, montant_dispo}], scope}
  // iter100i-P1 — Modal d'audit anomalies CAMT
  const [auditModal, setAuditModal] = useState(null);  // {data, loading}

  const lierFacture = async (pid, payloadOrId) => {
    // iter90 — Accept either a string facture_id (legacy) or a multi-payload object
    const body = typeof payloadOrId === "string"
      ? { facture_id: payloadOrId }
      : payloadOrId;
    try {
      const { data } = await api.patch(`/journal/provisoires/${pid}/lier-facture`, body);
      if (data.factures_linked > 1) {
        toast.success(`${data.factures_linked} factures liées (CHF ${data.total_alloue})${data.trop_percu ? ` · trop-perçu CHF ${data.trop_percu}` : ""}`);
      } else {
        toast.success(`Lié à ${data.facture_numero} (${data.was_fully_paid ? "payée" : "partiel"})`);
      }
      setLinkModal(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // iter341 — Rapprochement manuel d'un débit à une/plusieurs factures fournisseurs
  const lierFournisseurs = async (pid, body) => {
    try {
      const { data } = await api.patch(`/journal/provisoires/${pid}/lier-fournisseurs`, body);
      toast.success(
        `${data.nb_factures} facture(s) fournisseur rapprochée(s) · CHF ${fmtChf(data.total_alloue)}` +
        (data.ecart ? ` · écart CHF ${fmtChf(data.ecart)}` : ""),
      );
      setFfModal(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // iter90 — Relance le matching auto sur tous les provisoires en_attente
  const reMatch = async () => {
    try {
      const { data } = await api.post("/journal/provisoires/re-match");
      toast.success(`${data.newly_matched} transaction(s) rapprochée(s) · ${data.still_unmatched} restante(s)`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const validate = async (ids, ecart_compte_map = null) => {
    try {
      const payload = { ids };
      if (ecart_compte_map) payload.ecart_compte_map = ecart_compte_map;
      const { data } = await api.post("/journal/provisoires/valider", payload);
      // iter100h — Gestion des refus avec écart (Phase 2 UI)
      if (data.refuses && data.refuses.length > 0 && !ecart_compte_map) {
        const ecartRefuses = data.refuses.filter((r) => r.raison.toLowerCase().includes("écart") || r.raison.toLowerCase().includes("ecart"));
        if (ecartRefuses.length > 0) {
          setEcartModal({ refuses: ecartRefuses, scope: { type: "ids", ids } });
          if (data.valides > 0) toast.success(`${data.valides} validée(s), ${ecartRefuses.length} en attente de compte d'écart`);
          return;
        }
      }
      toast.success(`${data.valides} écriture(s) validée(s) en compta` + (data.refuses?.length ? ` — ${data.refuses.length} refus` : ""));
      setSelected(new Set());
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const validateAll = async (ecart_compte_map = null) => {
    try {
      const payload = { import_id: iid };
      if (ecart_compte_map) payload.ecart_compte_map = ecart_compte_map;
      const { data } = await api.post("/journal/provisoires/valider", payload);
      if (data.refuses && data.refuses.length > 0 && !ecart_compte_map) {
        const ecartRefuses = data.refuses.filter((r) => r.raison.toLowerCase().includes("écart") || r.raison.toLowerCase().includes("ecart"));
        if (ecartRefuses.length > 0) {
          setEcartModal({ refuses: ecartRefuses, scope: { type: "import", iid } });
          if (data.valides > 0) toast.success(`${data.valides} validée(s), ${ecartRefuses.length} en attente de compte d'écart`);
          return;
        }
      }
      toast.success(`${data.valides} écriture(s) validée(s)` + (data.refuses?.length ? ` — ${data.refuses.length} refus` : ""));
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // iter100i-P1 — Lance l'audit anomalies CAMT
  const openAudit = async () => {
    setAuditModal({ loading: true, data: null });
    try {
      const { data } = await api.get("/journal/camt/audit", { params: { import_id: iid } });
      setAuditModal({ loading: false, data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur audit");
      setAuditModal(null);
    }
  };

  const deleteImport = async () => {
    if (!window.confirm("Supprimer cet import et toutes ses écritures provisoires ?")) return;
    try {
      await api.delete(`/journal/camt/imports/${iid}`);
      toast.success("Import supprimé");
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (loading || !data) return <div className="text-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const toggleAll = () => {
    if (selected.size === data.provisoires.length) setSelected(new Set());
    else setSelected(new Set(data.provisoires.filter((p) => !p.validee_at && !p.ignore).map((p) => p.id)));
  };
  const toggle = (pid) => {
    const ns = new Set(selected);
    if (ns.has(pid)) ns.delete(pid); else ns.add(pid);
    setSelected(ns);
  };

  const nonValides = data.provisoires.filter((p) => !p.validee_at && !p.ignore);
  const valideables = nonValides.filter((p) => p.compte_contre_numero);

  return (
    <div className="space-y-4" data-testid="import-detail">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1">← Retour aux imports</button>
        {/* iter90 — Relance le matching auto sur tous les provisoires en_attente */}
        <Button
          variant="outline" size="sm"
          onClick={reMatch}
          className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
          data-testid="rematch-btn"
          title="Relance le matching automatique sur tous les paiements non rapprochés"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Relancer le rapprochement
        </Button>
      </div>

      <Card className="p-4 border-gray-200">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-bold text-lg text-gray-900">{data.fichier_nom}</div>
            <div className="text-xs text-gray-600 mt-1">
              Compte <span className="font-mono">{data.compte_bancaire_numero}</span> · {data.compte_bancaire_libelle} · IBAN <span className="font-mono">{data.iban_compte}</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Période {fmtDate(data.date_du)} → {fmtDate(data.date_au)} · {data.nb_mouvements_total} mvts · {data.nb_doublons_ignores} doublons ignorés · {data.nb_rapproches_auto} rapprochés auto
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/journal/camt/imports/${iid}/fichier`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm"><Download className="w-3.5 h-3.5 mr-1.5" /> Fichier original</Button>
            </a>
            {/* iter340 — export limité à cet import */}
            <ExportCamtBtn importId={iid} />
            <Button size="sm" variant="outline" onClick={relancerIA} disabled={iaLoading} className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="relancer-ia-btn">
              {iaLoading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <span className="mr-1">✨</span>}
              Suggérer avec IA
            </Button>
            {selected.size > 0 && (
              <Button size="sm" onClick={() => validate(Array.from(selected))} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="validate-selected">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Valider sélectionnées ({selected.size})
              </Button>
            )}
            {valideables.length > 0 && (
              <Button size="sm" variant="outline" onClick={() => validateAll()} data-testid="validate-all">
                Tout valider ({valideables.length} prêtes)
              </Button>
            )}
            {/* iter100i-P1 — Audit des anomalies de l'import */}
            <Button size="sm" variant="outline" onClick={openAudit} data-testid="open-audit" title="Audit anomalies (doublons, écarts, sur-encaissements...)">
              🔍 Audit
            </Button>
            {data.nb_valides === 0 && (
              <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={deleteImport}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2"><input type="checkbox" checked={selected.size > 0 && selected.size === nonValides.length} onChange={toggleAll} /></th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Libellé / contrepartie</th>
                <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Crédit</th>
                <th className="px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Débit</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Contrepartie compta</th>
                <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Facture / Statut</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.provisoires.map((p) => (
                <tr key={p.id} className={`border-t border-gray-100 ${p.validee_at ? "bg-blue-50/30" : p.ignore ? "bg-gray-50/50" : selected.has(p.id) ? "bg-emerald-50/30" : "hover:bg-gray-50/30"}`} data-testid={`prov-${p.id}`}>
                  <td className="px-2 py-2">
                    {!p.validee_at && !p.ignore && (
                      <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} data-testid={`prov-check-${p.id}`} />
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs whitespace-nowrap">{fmtDate(p.date_valeur)}</td>
                  <td className="px-2 py-2 text-xs">
                    <div className="font-semibold text-gray-800">{p.contrepartie_nom || "—"}</div>
                    <div className="text-gray-500 truncate max-w-[280px]">{p.libelle_bancaire}</div>
                    {p.reference_bancaire && <code className="text-[10px] text-gray-400">ref: {p.reference_bancaire}</code>}
                    {/* iter106 — Badges d'anomalies issues de l'audit */}
                    {auditByPid.has(p.id) && (
                      <div className="mt-1 space-y-1" data-testid={`prov-${p.id}-anomalies`}>
                        {auditByPid.get(p.id).map((a, i) => {
                          const sev = a.severity === "critique" ? "bg-red-50 border-red-300 text-red-800"
                            : a.severity === "important" ? "bg-orange-50 border-orange-300 text-orange-800"
                            : "bg-amber-50 border-amber-300 text-amber-800";
                          return (
                            <div key={i} className={`text-[10px] border rounded px-1.5 py-0.5 ${sev} max-w-[280px]`}>
                              ⚠️ {a.msg}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right font-mono text-xs text-emerald-700">{p.sens === "CRDT" ? `+${fmtChf(p.montant)}` : ""}</td>
                  <td className="px-2 py-2 text-right font-mono text-xs text-red-700">{p.sens === "DBIT" ? `-${fmtChf(p.montant)}` : ""}</td>
                  <td className="px-2 py-2 text-xs">
                    {p.validee_at ? (
                      <span className="text-gray-500">{p.compte_contre_numero} {p.compte_contre_libelle}</span>
                    ) : (
                      <div className="space-y-1">
                        <CompteSelect
                          value={p.compte_contre_numero || ""}
                          onValueChange={(v) => updateProv(p.id, { compte_contre_numero: v })}
                          options={plan.map((c) => ({ value: c.numero, label: `${c.numero} – ${c.libelle}` }))}
                          placeholder="Choisir..."
                          className="h-7 text-xs w-44"
                          data-testid={`prov-${p.id}-compte`}
                        />
                        {p.suggestion_ia && !p.compte_contre_numero && (
                          <button
                            onClick={() => updateProv(p.id, { compte_contre_numero: p.suggestion_ia.compte_numero })}
                            title={p.suggestion_ia.raisonnement}
                            className="text-[10px] bg-violet-50 hover:bg-violet-100 border border-violet-200 text-violet-800 rounded px-1.5 py-0.5 text-left w-44 truncate flex items-center gap-1"
                            data-testid={`prov-${p.id}-apply-ia`}
                          >
                            <span className="text-violet-500">✨ IA</span>
                            <span className="font-mono">{p.suggestion_ia.compte_numero}</span>
                            <span className="truncate">{p.suggestion_ia.compte_libelle}</span>
                            <span className="ml-auto text-[9px] text-violet-500 flex-shrink-0">{p.suggestion_ia.confiance}%</span>
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs">
                    {p.facture_suggere ? (
                      <div className="text-gray-700">
                        <code className="font-mono font-semibold">{p.facture_suggere.numero}</code>
                        <div className="text-[10px] text-gray-500">{p.facture_suggere.contact_label} · {fmtChf(p.facture_suggere.montant_brut)}</div>
                      </div>
                    ) : p.ff_suggere ? (
                      /* iter341 — suggestion facture fournisseur (DBIT) */
                      <div className="text-gray-700" data-testid={`prov-${p.id}-ff-suggere`}>
                        <code className="font-mono font-semibold">{p.ff_suggere.reference_interne}</code>
                        <div className="text-[10px] text-gray-500">
                          <span className="text-amber-700">Fournisseur</span> · {p.ff_suggere.fournisseur_nom || "—"} · {fmtChf(p.ff_suggere.montant_ttc)}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                    <Badge variant="outline" className={`${STATUT_PROV_COLORS[p.statut] || ""} text-[9px] mt-0.5`}>
                      {p.statut === "rapproche_auto" ? `À vérifier (${p.confidence}%)` :
                       p.statut === "rapproche_facture" ? "✓ Rapprochée" :
                       p.statut === "en_attente" ? "À vérifier" :
                       p.statut === "ignore" ? "Ignorée" :
                       p.statut === "validee" ? "Validée" : p.statut}
                    </Badge>
                    {/* iter86-bvr — Boutons Confirmer / Rejeter / Lier */}
                    {!p.validee_at && !p.ignore && p.sens === "CRDT" && p.statut !== "rapproche_facture" && (
                      <div className="mt-1 flex gap-1 flex-wrap">
                        {p.facture_id_suggere && (
                          <>
                            <button
                              onClick={() => confirmerFacture(p.id)}
                              className="text-[10px] bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 rounded px-1.5 py-0.5"
                              data-testid={`prov-${p.id}-confirmer`}
                              title="Confirmer le rapprochement et créer l'encaissement"
                            >
                              ✓ Confirmer
                            </button>
                            <button
                              onClick={() => rejeterFacture(p.id)}
                              className="text-[10px] bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 rounded px-1.5 py-0.5"
                              data-testid={`prov-${p.id}-rejeter`}
                              title="Rejeter la suggestion"
                            >
                              ✗ Rejeter
                            </button>
                          </>
                        )}
                        {!p.facture_id_suggere && (
                          <button
                            onClick={() => setLinkModal({
                              pid: p.id,
                              montant: p.montant,
                              // iter132.d — pré-charge les liens existants pour modification en lot
                              existingLinks: Array.isArray(p.factures_rapprochees) ? p.factures_rapprochees : [],
                            })}
                            className="text-[10px] bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-800 rounded px-1.5 py-0.5"
                            data-testid={`prov-${p.id}-lier`}
                            title={
                              Array.isArray(p.factures_rapprochees) && p.factures_rapprochees.length > 0
                                ? "Modifier l'allocation aux factures (remplace les liens existants)"
                                : "Lier manuellement à une facture"
                            }
                          >
                            🔗 {Array.isArray(p.factures_rapprochees) && p.factures_rapprochees.length > 0 ? "Modifier" : "Lier"}
                          </button>
                        )}
                      </div>
                    )}
                    {/* iter341 — Rapprochement manuel fournisseur (débits) */}
                    {!p.validee_at && !p.ignore && p.sens === "DBIT" && (
                      <div className="mt-1 flex gap-1 flex-wrap">
                        <button
                          onClick={() => setFfModal({ pid: p.id, montant: p.montant, suggere: p.ff_suggere || null })}
                          className="text-[10px] bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 rounded px-1.5 py-0.5"
                          data-testid={`prov-${p.id}-lier-fournisseur`}
                          title="Rapprocher ce paiement à une ou plusieurs factures fournisseurs"
                        >
                          🔗 Lier fournisseur
                        </button>
                      </div>
                    )}
                    {Array.isArray(p.fournisseurs_rapproches) && p.fournisseurs_rapproches.length > 0 && (
                      <div className="text-[10px] text-amber-800 mt-0.5 space-y-0.5" data-testid={`prov-${p.id}-ff-liens`}>
                        {p.fournisseurs_rapproches.map((fr) => (
                          <div key={fr.facture_fournisseur_id}>
                            → <a
                              href={`/factures-fournisseurs/${fr.facture_fournisseur_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:text-amber-900"
                            >{fr.reference_interne || fr.numero_facture}</a>
                            {" : "}<span className="font-mono">{fmtChf(fr.montant_alloue)}</span>
                            {!fr.soldee && <span className="text-gray-500"> (partiel)</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {(p.statut === "rapproche_facture" || p.validee_at) && p.facture_numero_rapproche && (
                      <div className="text-[10px] text-emerald-700 mt-0.5">
                        →&nbsp;
                        {p.facture_id_rapproche ? (
                          <a
                            href={`/factures/${p.facture_id_rapproche}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-emerald-900"
                            data-testid={`prov-${p.id}-facture-link`}
                          >
                            {p.facture_numero_rapproche}
                          </a>
                        ) : (
                          p.facture_numero_rapproche
                        )}
                      </div>
                    )}
                    {/* iter100c — Affiche les multi-liens + permet d'allouer le reste */}
                    {(p.statut === "rapproche_facture" || p.validee_at) && Array.isArray(p.factures_rapprochees) && p.factures_rapprochees.length > 0 && (
                      <div className="text-[10px] text-emerald-700 mt-0.5 space-y-0.5">
                        {p.factures_rapprochees.map((fr) => (
                          <div key={fr.facture_id} className="flex items-center gap-1">
                            <span>→ <a
                              href={`/factures/${fr.facture_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline hover:text-emerald-900"
                              data-testid={`prov-${p.id}-fr-link-${fr.facture_id}`}
                            >{fr.facture_numero}</a> : <span className="font-mono">{fmtChf(fr.montant_alloue)}</span></span>
                            {/* iter100h / iter135 — Bouton d'annulation tant que non validé.
                                Utilise le nouvel endpoint `lien-facture/{fid}` qui gère
                                aussi le cas du lien orphelin (post-dévalidation). */}
                            {!p.validee_at && (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Annuler le lien sur ${fr.facture_numero} (${fmtChf(fr.montant_alloue)} CHF) ?`)) return;
                                  try {
                                    await api.delete(`/journal/provisoires/${p.id}/lien-facture/${fr.facture_id}`);
                                    toast.success(`Lien annulé sur ${fr.facture_numero}`);
                                    fetchData();
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || "Erreur annulation");
                                  }
                                }}
                                className="text-[9px] text-red-500 hover:text-red-700 hover:bg-red-50 rounded px-1"
                                title="Annuler ce lien (tant que non validé)"
                                data-testid={`prov-${p.id}-delete-link-${fr.facture_id}`}
                              >
                                ✗
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {p.statut === "rapproche_facture" && !p.validee_at && !p.ignore && (p.trop_percu || 0) > 0.05 && (
                      <div className="mt-1 flex gap-1 flex-wrap items-center">
                        <span className="text-[10px] text-amber-700">Reste à allouer : <b className="font-mono">{fmtChf(p.trop_percu)}</b></span>
                        <button
                          onClick={() => setLinkModal({ pid: p.id, montant: p.trop_percu, append: true })}
                          className="text-[10px] bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 rounded px-1.5 py-0.5"
                          data-testid={`prov-${p.id}-allouer-reste`}
                          title="Allouer le montant restant à d'autres factures"
                        >
                          🔗 Allouer le reste
                        </button>
                        {/* iter174 — Boucler le reste directement sur un compte comptable
                            (produit 3200/3400 pour paiement sans facture, escompte, arrondi…) */}
                        <button
                          onClick={() => setEcartModal({
                            refuses: [{
                              id: p.id,
                              date_valeur: p.date_valeur,
                              libelle: p.libelle_bancaire || p.reference_bancaire || "Écart",
                              montant_dispo: p.trop_percu,
                            }],
                            scope: { type: "ids", ids: [p.id] },
                          })}
                          className="text-[10px] bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-800 rounded px-1.5 py-0.5"
                          data-testid={`prov-${p.id}-boucler-compte`}
                          title="Boucler le reste sur un compte comptable (produit, escompte, écart…)"
                        >
                          🔧 Boucler sur compte
                        </button>
                      </div>
                    )}
                    {/* iter105 — Bouton de validation directe par ligne (évite le batch select) */}
                    {p.statut === "rapproche_facture" && !p.validee_at && !p.ignore && (p.trop_percu || 0) <= 0.05 && (
                      <div className="mt-1">
                        <button
                          onClick={() => validate([p.id])}
                          className="text-[10px] bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-800 font-medium rounded px-2 py-0.5"
                          data-testid={`prov-${p.id}-valider`}
                          title="Valider cette écriture comptable directement"
                        >
                          ✓ Valider cette ligne
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs">
                    <div className="flex gap-1 items-center">
                      <PiecesButton parentId={p.id} pieces={p.pieces || []} kind="provisoire" onChange={fetchData} />
                      {/* Bouton créer dépense rapide depuis ce provisoire DBIT (sortie d'argent) */}
                      {!p.validee_at && !p.ignore && p.sens === "DBIT" && (
                        <button
                          onClick={async () => {
                            try {
                              const { data } = await api.post("/depenses-rapides/creer-depuis-provisoire", { provisoire_id: p.id });
                              toast.success(`Dépense ${data.reference_interne} créée — complétez et validez`);
                              window.location.href = `/depenses-rapides/${data.id}`;
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Erreur");
                            }
                          }}
                          title="Créer une dépense rapide pré-remplie depuis ce mouvement"
                          className="text-gray-400 hover:text-orange-600 p-1"
                          data-testid={`prov-${p.id}-creer-depense`}
                        >
                          <Wallet className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!p.validee_at && !p.ignore && (
                        <button onClick={() => updateProv(p.id, { ignore: true })} title="Ignorer cette ligne" className="text-gray-400 hover:text-red-600 p-1" data-testid={`prov-${p.id}-ignore`}>
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {p.ignore && (
                        <button onClick={() => updateProv(p.id, { ignore: false })} className="text-gray-400 hover:text-emerald-600 text-xs">Restaurer</button>
                      )}
                      {/* iter106 — Déverrouillage direct pour modifier une écriture validée */}
                      {p.validee_at && (
                        <button
                          onClick={async () => {
                            if (!window.confirm(
                              "Déverrouiller cette écriture pour la modifier ?\n\nCela va :\n" +
                              "• Supprimer les écritures comptables créées\n" +
                              "• Retirer les encaissements liés sur les factures\n" +
                              "• Remettre la ligne modifiable (statut « rapprochée »)\n\n" +
                              "Vous pourrez ensuite corriger le compte, changer la facture, et re-valider."
                            )) return;
                            try {
                              const res = await api.post(`/journal/provisoires/${p.id}/devalider`);
                              toast.success(
                                `Déverrouillée · ${res.data.ecritures_supprimees} écriture(s) annulée(s)`,
                                { duration: 5000 },
                              );
                              fetchData();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Erreur");
                            }
                          }}
                          title="Modifier cette écriture validée (déverrouille puis re-valide)"
                          className="text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded p-1 text-xs font-medium"
                          data-testid={`prov-${p.id}-modifier`}
                        >
                          🔓 Modifier
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {/* iter86-bvr — Modal de liaison manuelle à une facture ouverte */}
      {linkModal && (
        <LinkFactureModal
          montant={linkModal.montant}
          append={!!linkModal.append}
          existingLinks={linkModal.existingLinks || []}
          onClose={() => setLinkModal(null)}
          onPick={(payload) => lierFacture(linkModal.pid, payload)}
        />
      )}

      {/* iter341 — Modal de rapprochement manuel aux factures fournisseurs */}
      {ffModal && (
        <LinkFournisseurModal
          montant={ffModal.montant}
          suggere={ffModal.suggere}
          onClose={() => setFfModal(null)}
          onSubmit={(body) => lierFournisseurs(ffModal.pid, body)}
        />
      )}

      {/* iter100h-P2 — Modal sélecteur de compte d'écart pour validation */}
      {ecartModal && (
        <EcartCompteModal
          refuses={ecartModal.refuses}
          onClose={() => setEcartModal(null)}
          onSubmit={(map) => {
            const cb = ecartModal.scope.type === "import" ? validateAll : (m) => validate(ecartModal.scope.ids, m);
            cb(map);
            setEcartModal(null);
          }}
        />
      )}

      {/* iter100i-P1 — Modal d'audit anomalies CAMT */}
      {auditModal && (
        <AuditCamtModal
          data={auditModal.data}
          loading={auditModal.loading}
          onClose={() => setAuditModal(null)}
          onReload={openAudit}
        />
      )}
    </div>
  );
}

// iter90 — Modal multi-select pour rapprocher 1 paiement à 1+ factures
// iter100c — Cache de sélection persistant (indépendant de la recherche)
//          + support du mode "append" (re-link sur reste après allocation partielle)
// iter132.d — pré-charge `existingLinks` pour modification en lot (envoie `replace=true`)
//           + mode manuel hybride : laisser un champ vide = auto-cascade sur le reste
function LinkFactureModal({ montant, append = false, existingLinks = [], onClose, onPick }) {
  const isModifying = !append && existingLinks.length > 0;
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // iter202 — Pagination : "Charger plus" pour les clients avec beaucoup de factures
  const [totalMatching, setTotalMatching] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 100;
  // Pré-coche les factures déjà liées (modification d'allocation)
  const [selected, setSelected] = useState(() => {
    const s = {};
    existingLinks.forEach((fr) => { if (fr.facture_id) s[fr.facture_id] = true; });
    return s;
  });
  const [selectedFactsCache, setSelectedFactsCache] = useState({});
  // En modification : démarre en mode "manual" avec les montants existants pré-remplis
  const [splitMode, setSplitMode] = useState(isModifying ? "manual" : "auto");
  const [manualAmounts, setManualAmounts] = useState(() => {
    const m = {};
    if (isModifying) {
      existingLinks.forEach((fr) => {
        if (fr.facture_id && (fr.montant_alloue || 0) > 0) {
          m[fr.facture_id] = String(fr.montant_alloue.toFixed ? fr.montant_alloue.toFixed(2) : fr.montant_alloue);
        }
      });
    }
    return m;
  });
  // iter132.d — Bootstrap : si on modifie une allocation existante, on charge les
  // détails complets des factures déjà liées pour les afficher en haut de la modal.
  useEffect(() => {
    if (!isModifying || existingLinks.length === 0) return;
    let cancelled = false;
    (async () => {
      const out = {};
      for (const fr of existingLinks) {
        if (!fr.facture_id || selectedFactsCache[fr.facture_id]) continue;
        try {
          const { data } = await api.get(`/factures/${fr.facture_id}`);
          // iter132.d — reste_a_payer ré-injecté : la facture inclut déjà l'encaissement
          // existant lié à cette provisoire ; lors d'un `replace`, ce montant sera libéré.
          // Donc le "reste max éditable" = reste_a_payer_actuel + montant_déjà_alloué_par_cette_provisoire
          const restePuriste = Math.max(0, (data.montant_brut || 0) - (data.encaissements || [])
            .filter((e) => e.journal_provisoire_id !== undefined)
            .reduce((s, e) => s + (e.montant || 0), 0));
          const resteEditable = restePuriste + (fr.montant_alloue || 0);
          out[fr.facture_id] = {
            id: data.id,
            numero: data.numero,
            contact_label: data.contact_label || data.client_nom || "",
            montant_brut: data.montant_brut,
            reste_a_payer: resteEditable,
          };
        } catch { /* ignore */ }
      }
      if (!cancelled && Object.keys(out).length > 0) {
        setSelectedFactsCache((c) => ({ ...out, ...c }));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/factures/ouvertes`, { params: { q, limit: PAGE_SIZE, offset: 0 } });
        setResults(data.factures || []);
        setTotalMatching(data.total_matching || 0);
        setHasMore(!!data.has_more);
      } catch {
        setResults([]);
        setTotalMatching(0);
        setHasMore(false);
      } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  // iter202 — Charge la page suivante (append aux résultats)
  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get(`/factures/ouvertes`, {
        params: { q, limit: PAGE_SIZE, offset: results.length },
      });
      setResults((prev) => [...prev, ...(data.factures || [])]);
      setHasMore(!!data.has_more);
    } catch { /* silencieux */ }
    finally { setLoadingMore(false); }
  };

  // iter100c — Maintient le cache : ajoute/retire la facture quand on coche/décoche
  const toggleSelected = (facture, checked) => {
    setSelected((s) => ({ ...s, [facture.id]: checked }));
    setSelectedFactsCache((c) => {
      const next = { ...c };
      if (checked) next[facture.id] = facture;
      else delete next[facture.id];
      return next;
    });
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const selectedFacts = selectedIds.map((id) => selectedFactsCache[id]).filter(Boolean);
  const totalDu = selectedFacts.reduce((s, f) => s + (f.reste_a_payer || 0), 0);

  // iter132.d — Mode hybride : un champ vide en mode manuel = auto-cascade sur le reste.
  // On calcule à la fois la somme des montants EXPLICITES + ce que recevra chaque
  // facture « auto » pour afficher le footer correctement.
  const isExplicit = (v) => v !== undefined && v !== null && String(v).trim() !== "";
  const explicitTotal = selectedIds.reduce(
    (s, fid) => (isExplicit(manualAmounts[fid]) ? s + parseFloat(manualAmounts[fid] || 0) : s),
    0,
  );
  const autoIds = selectedIds.filter((fid) => !isExplicit(manualAmounts[fid]));
  // Cascade le reste sur les factures sans montant explicite (par ancienneté approchée
  // via l'ordre du cache — le backend re-trie par date_emission de toute façon).
  const autoFillMap = (() => {
    if (splitMode !== "manual" || autoIds.length === 0) return {};
    let resteAuto = Math.max(0, montant - explicitTotal);
    const map = {};
    const sorted = autoIds
      .map((fid) => selectedFactsCache[fid])
      .filter(Boolean)
      .sort((a, b) => (a.date_emission || "").localeCompare(b.date_emission || ""));
    for (const f of sorted) {
      if (resteAuto <= 0.005) { map[f.id] = 0; continue; }
      const alloc = Math.min(resteAuto, f.reste_a_payer || 0);
      map[f.id] = parseFloat(alloc.toFixed(2));
      resteAuto = parseFloat((resteAuto - alloc).toFixed(2));
    }
    return map;
  })();
  const totalAutoFilled = Object.values(autoFillMap).reduce((s, v) => s + v, 0);
  const totalManual = explicitTotal + totalAutoFilled;
  const totalAlloue = splitMode === "manual" ? totalManual : Math.min(montant, totalDu);
  const ecart = montant - totalAlloue;
  const ecartColor = Math.abs(ecart) < 0.05 ? "text-emerald-700" : (ecart > 0 ? "text-amber-700" : "text-red-700");

  const handleSubmit = () => {
    if (selectedIds.length === 0) return;
    const payload = { facture_ids: selectedIds, split_mode: splitMode };
    if (splitMode === "manual") {
      // iter132.d — Envoie `null` pour les champs vides → backend cascade ces factures
      payload.amounts = selectedIds.map((fid) => ({
        facture_id: fid,
        amount: isExplicit(manualAmounts[fid]) ? parseFloat(manualAmounts[fid]) : null,
      }));
    }
    if (append) payload.append = true;
    if (isModifying) payload.replace = true;
    onPick(payload);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="link-facture-modal">
        <DialogHeader>
          <DialogTitle>
            {append
              ? "Allouer le reste à d'autres factures"
              : (isModifying
                  ? `Modifier l'allocation (${existingLinks.length} facture${existingLinks.length > 1 ? "s" : ""} actuellement liée${existingLinks.length > 1 ? "s" : ""})`
                  : "Lier le paiement à une ou plusieurs factures")}
          </DialogTitle>
        </DialogHeader>
        {append && (
          <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1.5 mb-2">
            Montant restant non alloué : <b>CHF {fmtChf(montant)}</b> · Sélectionne d'autres factures pour le ventiler.
          </div>
        )}
        {isModifying && (
          <div className="text-xs bg-blue-50 border border-blue-200 text-blue-900 rounded px-2 py-1.5 mb-2" data-testid="modify-allocation-banner">
            <b>{existingLinks.length} facture{existingLinks.length > 1 ? "s" : ""} déjà rapprochée{existingLinks.length > 1 ? "s" : ""}</b> — pré-cochées avec leurs montants actuels. Modifier (cocher/décocher, changer les montants) puis cliquer <b>Lier</b> remplacera l&apos;allocation existante. Astuce : laisse un champ <b>vide</b> en mode manuel pour cascader automatiquement le reste sur cette facture.
          </div>
        )}
        <div className="flex gap-2 mb-3 items-center flex-wrap">
          <Input
            autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Numéro, client ou montant…"
            className="flex-1 min-w-[200px]" data-testid="link-facture-search"
          />
          <div className="flex gap-1 text-xs">
            <button
              onClick={() => setSplitMode("auto")}
              className={`px-2 py-1 rounded border ${splitMode === "auto" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-gray-200 text-gray-600"}`}
              data-testid="split-mode-auto"
            >Auto (plus ancienne d'abord)</button>
            <button
              onClick={() => setSplitMode("manual")}
              className={`px-2 py-1 rounded border ${splitMode === "manual" ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-white border-gray-200 text-gray-600"}`}
              data-testid="split-mode-manual"
            >Manuel</button>
          </div>
        </div>
        <div className="max-h-[400px] overflow-y-auto" data-testid="reconciliation-multi-select">
          {(() => {
            // iter132.d — Affiche d'abord les factures déjà sélectionnées (ex. liens préchargés
            // en modification) qui ne sont pas présentes dans les résultats de recherche,
            // pour qu'elles restent visibles et modifiables.
            const resultIds = new Set(results.map((r) => r.id));
            const extraSelected = selectedIds
              .filter((id) => !resultIds.has(id) && selectedFactsCache[id])
              .map((id) => selectedFactsCache[id]);
            const renderRows = [...extraSelected, ...results];
            if (loading && renderRows.length === 0) {
              return <div className="text-center text-gray-500 py-6 text-sm">Recherche…</div>;
            }
            if (renderRows.length === 0) {
              return <div className="text-center text-gray-500 py-6 text-sm">Aucune facture ouverte</div>;
            }
            return (
            <>
            <ul className="divide-y divide-gray-100">
              {renderRows.map((f) => (
                <li key={f.id} className={`py-2 px-2 flex items-center gap-2 text-xs ${selected[f.id] ? "bg-emerald-50" : "hover:bg-gray-50"}`}
                    data-testid={`link-facture-result-${f.id}`}>
                  <input
                    type="checkbox"
                    checked={!!selected[f.id]}
                    onChange={(e) => toggleSelected(f, e.target.checked)}
                    className="shrink-0"
                    data-testid={`link-checkbox-${f.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono font-semibold text-sm">{f.numero}</div>
                    <div className="text-xs text-gray-600 truncate">{f.contact_label || "—"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-sm">CHF {fmtChf(f.montant_brut)}</div>
                    {/* iter100h — Solde avant/après quand sélectionné en manuel */}
                    {splitMode === "manual" && selected[f.id] && parseFloat(manualAmounts[f.id] || 0) > 0 ? (
                      <div className="text-[10px] text-gray-500">
                        Reste: <span className="line-through opacity-60">{fmtChf(f.reste_a_payer)}</span>
                        {" → "}
                        <span className="font-semibold text-emerald-700">
                          {fmtChf(Math.max(0, (f.reste_a_payer || 0) - parseFloat(manualAmounts[f.id] || 0)))}
                        </span>
                      </div>
                    ) : (
                      <div className="text-[10px] text-gray-500">Reste: <span className="font-semibold">{fmtChf(f.reste_a_payer)}</span></div>
                    )}
                  </div>
                  {splitMode === "manual" && selected[f.id] && (() => {
                    // iter100h — Borne la saisie à min(reste_facture, dispo_transaction)
                    // iter132.d — Ignore les factures « auto » dans le calcul de l'occupé.
                    const otherAlloc = selectedIds
                      .filter((sid) => sid !== f.id && isExplicit(manualAmounts[sid]))
                      .reduce((s, sid) => s + parseFloat(manualAmounts[sid] || 0), 0);
                    const dispoTrans = Math.max(0, montant - otherAlloc);
                    const maxForThis = Math.min(f.reste_a_payer || 0, dispoTrans);
                    const cur = parseFloat(manualAmounts[f.id] || 0);
                    const over = isExplicit(manualAmounts[f.id]) && cur > maxForThis + 0.05;
                    const autoVal = autoFillMap[f.id];
                    const isAuto = !isExplicit(manualAmounts[f.id]);
                    return (
                      <div className="flex flex-col items-end gap-0.5">
                        <Input
                          type="number" step="0.01" min="0" max={maxForThis}
                          placeholder={isAuto ? "auto" : maxForThis.toFixed(2)}
                          value={manualAmounts[f.id] || ""}
                          onChange={(e) => setManualAmounts((m) => ({ ...m, [f.id]: e.target.value }))}
                          className={`w-24 h-7 text-xs ${over ? "border-red-500 bg-red-50" : (isAuto ? "border-dashed border-blue-300 bg-blue-50/40" : "")}`}
                          data-testid={`manual-amount-${f.id}`}
                          title={isAuto ? `Auto-cascade : ${(autoVal || 0).toFixed(2)} CHF (max ${maxForThis.toFixed(2)})` : `Max: ${maxForThis.toFixed(2)} CHF`}
                        />
                        {isAuto && (autoVal !== undefined) && (
                          <span className="text-[9px] text-blue-700 italic" data-testid={`auto-cascade-${f.id}`}>
                            → {fmtChf(autoVal)}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </li>
              ))}
            </ul>
            {/* iter202 — Pagination + Charger plus */}
            {(totalMatching > 0 || hasMore) && (
              <div className="mt-2 px-2 py-2 text-xs text-gray-600 flex items-center justify-between border-t" data-testid="link-facture-pagination">
                <span>
                  {results.length}{totalMatching ? ` / ${totalMatching}` : ""} factures
                </span>
                {hasMore && (
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="text-emerald-700 hover:text-emerald-900 font-medium disabled:opacity-60"
                    data-testid="link-facture-load-more"
                  >
                    {loadingMore ? "Chargement…" : "Charger plus"}
                  </button>
                )}
              </div>
            )}
            </>
            );
          })()}
        </div>
        {/* iter100h — Footer enrichi : Disponible, écart coloré, blocage si over-alloc */}
        <div className={`mt-3 p-2 rounded border text-sm font-mono flex justify-between items-center ${ecartColor.replace("text-", "border-").replace("700", "300")} bg-gray-50`}>
          <span>
            Sélectionné : <b>CHF {fmtChf(totalAlloue)}</b> ({selectedIds.length} fact.) / Disponible : <b>CHF {fmtChf(montant)}</b>
          </span>
          <span className={`${ecartColor} font-semibold`}>
            {Math.abs(ecart) < 0.05 ? "✓ Complet" : ecart > 0 ? `Reste à allouer +${fmtChf(ecart)}` : `🚫 Sur-allocation ${fmtChf(-ecart)}`}
          </span>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedIds.length === 0 || ecart < -0.05}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="reconciliation-submit"
            title={ecart < -0.05 ? "Sur-allocation interdite" : ""}
          >
            {isModifying
              ? `Remplacer l'allocation (${selectedIds.length} facture${selectedIds.length > 1 ? "s" : ""})`
              : `Lier ${selectedIds.length} facture${selectedIds.length > 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PiecesButton({ parentId, pieces, kind, onChange }) {
  // Bouton paperclip + dialog mini d'upload + liste
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const route = kind === "provisoire" ? `/journal/provisoires/${parentId}/pieces` : `/journal/ecritures/${parentId}/pieces`;
      await api.post(route, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pièce ajoutée");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setUploading(false); }
  };
  const download = (pid) => {
    const token = localStorage.getItem("cm_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/journal/pieces/${pid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      });
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className={`p-1 rounded relative ${pieces.length ? "text-[#52a675]" : "text-gray-300 hover:text-gray-600"}`} title={`${pieces.length} pièce(s)`} data-testid={`pieces-btn-${parentId}`}>
        <Paperclip className="w-3.5 h-3.5" />
        {pieces.length > 0 && <span className="absolute -top-1 -right-1 bg-[#52a675] text-white rounded-full text-[8px] w-3 h-3 flex items-center justify-center font-bold">{pieces.length}</span>}
      </button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Pièces jointes</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {pieces.length === 0 && <div className="text-xs text-gray-400 text-center py-4">Aucune pièce</div>}
            {pieces.map((p) => (
              <div key={p.id} className="flex items-center gap-2 p-2 border border-gray-200 rounded text-xs">
                <FileText className="w-4 h-4 text-gray-400" />
                <div className="flex-1 truncate">{p.filename}<div className="text-[10px] text-gray-400">{Math.round(p.size / 1024)} Ko · {p.uploaded_by_nom}</div></div>
                <button onClick={() => download(p.id)} className="text-[#52a675] hover:underline"><Eye className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <label className="block">
              <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
              <span className="block w-full text-center border-2 border-dashed border-gray-300 rounded p-3 text-xs text-gray-600 hover:border-[#52a675] hover:bg-[#52a675]/5 cursor-pointer">
                {uploading ? <><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> Upload...</> : <>+ Ajouter un PDF / image</>}
              </span>
            </label>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Fermer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


// iter100h-P2 — Modal pour assigner un compte d'écart aux provisoires refusés
// iter174 — Ajout des comptes de produits 3200 / 3400 pour boucler un trop-perçu
// sans facture (paiement client sans facture d'origine, produit divers…).
const COMPTES_ECART_PREDEFINIS = [
  { num: "3200", lib: "Produit ventes atelier (MO)" },
  { num: "3400", lib: "Produit ventes pièces" },
  { num: "6701", lib: "Arrondi de paiement" },
  { num: "3801", lib: "Escompte accordé clients" },
  { num: "3800", lib: "Pertes sur créances" },
  { num: "6900", lib: "Frais bancaires" },
  { num: "2030", lib: "Acomptes clients" },
];
function EcartCompteModal({ refuses, onClose, onSubmit }) {
  const [map, setMap] = useState({});
  const [customNum, setCustomNum] = useState("");
  const [customLib, setCustomLib] = useState("");

  const setForAll = (num) => {
    const m = {};
    refuses.forEach((r) => { m[r.id] = num; });
    setMap(m);
  };

  const handleSubmit = () => {
    const allAssigned = refuses.every((r) => map[r.id]);
    if (!allAssigned) {
      toast.error("Assigne un compte pour chaque provisoire");
      return;
    }
    onSubmit(map);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ecart-compte-modal">
        <DialogHeader>
          <DialogTitle>Compte d'écart requis pour la validation</DialogTitle>
        </DialogHeader>
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1.5 mb-3">
          <b>{refuses.length}</b> transaction{refuses.length > 1 ? "s" : ""} ont un écart non assigné. Choisis un compte d'imputation pour chacune (escompte, arrondi, frais bancaires...).
        </div>

        <div className="mb-3 flex gap-2 flex-wrap text-xs">
          <span className="text-gray-500 self-center">Assigner à toutes :</span>
          {COMPTES_ECART_PREDEFINIS.map((c) => (
            <button key={c.num + c.lib}
              onClick={() => setForAll(c.num)}
              className="px-2 py-1 rounded border border-gray-200 hover:bg-emerald-50 hover:border-emerald-300"
              data-testid={`ecart-quick-${c.num}`}
            >
              {c.num} {c.lib}
            </button>
          ))}
        </div>

        <div className="max-h-[300px] overflow-y-auto border rounded">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr><th className="text-left p-2">Date</th><th className="text-left p-2">Libellé</th><th className="text-right p-2">Écart</th><th className="p-2">Compte</th></tr>
            </thead>
            <tbody>
              {refuses.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="p-2 font-mono">{r.date_valeur || "—"}</td>
                  <td className="p-2 truncate max-w-[200px]" title={r.libelle}>{r.libelle || "—"}</td>
                  <td className="p-2 text-right font-mono text-amber-700">{(r.montant_dispo || 0).toFixed(2)} CHF</td>
                  <td className="p-2">
                    <Input
                      type="text"
                      value={map[r.id] || ""}
                      onChange={(e) => setMap((m) => ({ ...m, [r.id]: e.target.value.trim() }))}
                      placeholder="N° compte"
                      className="h-7 text-xs w-24 font-mono"
                      data-testid={`ecart-input-${r.id}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex gap-2 items-end text-xs">
          <div className="flex-1">
            <Label className="text-xs">Compte personnalisé (optionnel) :</Label>
            <div className="flex gap-1 mt-1">
              <Input value={customNum} onChange={(e) => setCustomNum(e.target.value.trim())} placeholder="6701" className="w-20 h-7 text-xs font-mono" />
              <Input value={customLib} onChange={(e) => setCustomLib(e.target.value)} placeholder="Libellé" className="flex-1 h-7 text-xs" />
              <Button size="sm" variant="outline" onClick={() => customNum && setForAll(customNum)} disabled={!customNum}>Appliquer</Button>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ecart-submit">
            Valider avec écart
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// iter104 — Modal d'audit anomalies CAMT — refonte UX claire pour personne lambda
// Affiche des cartes lisibles + badges sévérité + boutons d'action
// (Voir la facture / Déverrouiller le mouvement CAMT)
const AUDIT_CATEGORIES_V2 = [
  {
    key: "doublons_acct_servicer_ref",
    label: "Doublons de référence bancaire",
    explainer: "Le même mouvement bancaire a été importé 2 fois. C'est le plus grave car ça crée des écritures fantômes.",
    severity: "critique",
    icon: "🔴",
  },
  {
    key: "sur_encaissements_factures",
    label: "Factures sur-encaissées",
    explainer: "Une facture a reçu plus d'argent que son montant brut. Souvent un doublon d'encaissement.",
    severity: "critique",
    icon: "🔴",
  },
  {
    key: "incoherences_alloue_superieur_montant",
    label: "Mouvements sur-alloués",
    explainer: "Une transaction bancaire a été allouée à plus que son montant total entre plusieurs factures.",
    severity: "critique",
    icon: "🔴",
  },
  {
    key: "doublons_dedup_key",
    label: "Doublons (date + montant + référence)",
    explainer: "Détection probable de doublon par signature. À vérifier — parfois faux positif si vraie transaction répétée.",
    severity: "important",
    icon: "🟠",
  },
  {
    key: "factures_payees_mais_reste",
    label: "Factures « payée » avec reste à payer",
    explainer: "Le statut dit payée mais il manque encore de l'argent. Incohérence à corriger.",
    severity: "important",
    icon: "🟠",
  },
  {
    key: "ecarts_validation_sans_compte",
    label: "Écarts validés sans compte d'imputation",
    explainer: "Un mouvement validé avec un écart (escompte/arrondi) sans compte attribué. Cf. Phase B pour suggestions auto.",
    severity: "attention",
    icon: "🟡",
  },
  {
    key: "provisoires_orphelins",
    label: "Rapprochements oubliés (non validés)",
    explainer: "Une facture est liée à un mouvement mais l'utilisateur a oublié de valider. À reprendre dans l'onglet rapprochement.",
    severity: "attention",
    icon: "🟡",
  },
];
const SEVERITY_STYLES = {
  critique: "border-red-300 bg-red-50",
  important: "border-orange-300 bg-orange-50",
  attention: "border-amber-300 bg-amber-50",
};

function fmtChfShort(n) {
  return (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// iter105 — Sens lisible (Débit/Crédit) vs ambigu "reçu"
function sensLabel(sens, fallback = "Mouvement") {
  if (sens === "DBIT") return "Débit (sortie)";
  if (sens === "CRDT") return "Crédit (entrée)";
  return fallback;
}

// Phrase humaine décrivant chaque type d'anomalie
function describeAnomaly(catKey, row) {
  if (catKey === "sur_encaissements_factures") {
    const exact = Math.abs(row.excedent - row.montant_brut) < 0.06;
    const txt = `Facture ${row.numero || "(sans n°)"} : ${fmtChfShort(row.total_encaisse)} CHF encaissés pour ${fmtChfShort(row.montant_brut)} CHF dus`;
    return exact
      ? `${txt} — surplus = montant exact ⇒ DOUBLON probable.`
      : `${txt} — surplus de ${fmtChfShort(row.excedent)} CHF.`;
  }
  if (catKey === "incoherences_alloue_superieur_montant") {
    return `${sensLabel(row.sens)} du ${fmtDate(row.date_valeur)} : ${fmtChfShort(row.alloue)} CHF alloués pour ${fmtChfShort(row.montant)} CHF — surplus ${fmtChfShort(row.excedent)} CHF.`;
  }
  if (catKey === "doublons_acct_servicer_ref" || catKey === "doublons_dedup_key") {
    return `${row.ids?.length || 2} mouvements identiques du ${fmtDate(row.date_valeur)} pour ${fmtChfShort(row.montant)} CHF — « ${(row.libelle || "").slice(0, 100)} »`;
  }
  if (catKey === "factures_payees_mais_reste") {
    return `Facture ${row.numero || "(sans n°)"} : statut « payée » mais reste ${fmtChfShort(row.reste)} CHF à encaisser.`;
  }
  if (catKey === "ecarts_validation_sans_compte") {
    return `${sensLabel(row.sens)} du ${fmtDate(row.date_valeur)} : ${fmtChfShort(row.montant)} CHF, alloué ${fmtChfShort(row.alloue)} CHF — écart ${fmtChfShort(row.ecart)} CHF sans compte d'imputation.`;
  }
  if (catKey === "provisoires_orphelins") {
    return `${sensLabel(row.sens)} du ${fmtDate(row.date_valeur)} ${fmtChfShort(row.montant)} CHF, ${row.nb_factures} facture(s) liée(s), pas encore validé.`;
  }
  return JSON.stringify(row);
}

// iter105 — Bouton de déverrouillage direct pour les anomalies basées sur provisoire
// Évite le détour par le sous-modal "UnlockProvisoiresModal" quand il n'y a
// qu'un seul provisoire à déverrouiller.
function DirectUnlockButton({ pid, onDone, label = "🔓 Déverrouiller" }) {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (!window.confirm(
      "Confirmer le déverrouillage de ce mouvement ?\n\nCela va :\n" +
      "• Supprimer les écritures comptables créées\n" +
      "• Retirer les encaissements liés sur les factures\n" +
      "• Remettre le mouvement modifiable dans l'onglet rapprochement\n\n" +
      "Vous pourrez ensuite le re-valider avec le bon compte d'imputation."
    )) return;
    setBusy(true);
    try {
      const res = await api.post(`/journal/provisoires/${pid}/devalider`);
      toast.success(
        `Mouvement déverrouillé · ${res.data.ecritures_supprimees} écriture(s) annulée(s)`,
        { duration: 5000 },
      );
      if (onDone) setTimeout(onDone, 400);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du déverrouillage");
    }
    setBusy(false);
  };

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-100 border border-amber-300 text-amber-800 hover:bg-amber-200 disabled:opacity-50"
      data-testid={`audit-direct-unlock-${pid}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : label}
    </button>
  );
}

function AuditCamtModal({ data, loading, onClose, onReload }) {
  const [expanded, setExpanded] = useState({});
  const [unlockTarget, setUnlockTarget] = useState(null); // {facture_id, numero}
  const totaux = data?.totaux || {};
  const anomalies = data?.anomalies || {};
  const totalAnomalies = totaux.total_anomalies || 0;

  // Mapping cat.key → anomalies key (corrige le nom verbeux backend)
  const getList = (catKey) => {
    if (catKey === "provisoires_orphelins")
      return anomalies.provisoires_orphelins_rapproche_facture_non_valides || [];
    return anomalies[catKey] || [];
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="audit-camt-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Audit des anomalies CAMT
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-10 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            <div className="mt-2 text-sm">Analyse en cours…</div>
          </div>
        ) : !data ? null : (
          <>
            {/* Bandeau résumé */}
            <div className={`p-3 rounded-lg border-2 ${totalAnomalies === 0 ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"} flex items-center justify-between`}>
              <div className="text-sm">
                <div className="font-medium text-gray-800">
                  {data.perimetre.nb_provisoires_analyses} mouvement{data.perimetre.nb_provisoires_analyses > 1 ? "s" : ""} analysé{data.perimetre.nb_provisoires_analyses > 1 ? "s" : ""}
                </div>
                <div className="text-xs text-gray-600">
                  {data.perimetre.import_id ? "Sur cet import uniquement" : "Sur tous les imports"}
                </div>
              </div>
              <div className="text-right">
                {totalAnomalies === 0 ? (
                  <div className="text-emerald-700 font-bold text-lg">✅ Aucune anomalie</div>
                ) : (
                  <div className="text-red-700 font-bold text-2xl" data-testid="audit-total-anomalies">{totalAnomalies}</div>
                )}
                {totalAnomalies > 0 && <div className="text-xs text-red-600">anomalie{totalAnomalies > 1 ? "s" : ""} à traiter</div>}
              </div>
            </div>

            {/* Si zéro anomalie : état rassurant */}
            {totalAnomalies === 0 && (
              <div className="text-center py-10 text-emerald-700">
                <div className="text-5xl mb-3">🎉</div>
                <div className="text-base font-medium">Tout est cohérent !</div>
                <div className="text-sm text-emerald-600 mt-1">Aucun doublon, sur-encaissement ou écriture incohérente.</div>
              </div>
            )}

            {/* Cartes par catégorie */}
            {totalAnomalies > 0 && (
              <div className="mt-4 space-y-3" data-testid="audit-categories">
                {AUDIT_CATEGORIES_V2.map((cat) => {
                  const list = getList(cat.key);
                  if (!list.length) return null;
                  const isOpen = expanded[cat.key];
                  return (
                    <div key={cat.key} className={`border-2 rounded-lg ${SEVERITY_STYLES[cat.severity]} overflow-hidden`} data-testid={`audit-cat-${cat.key}`}>
                      <button
                        onClick={() => setExpanded((e) => ({ ...e, [cat.key]: !e[cat.key] }))}
                        className="w-full px-4 py-3 text-left flex justify-between items-center hover:bg-black/5"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 flex items-center gap-2">
                            <span className="text-lg">{cat.icon}</span>
                            <span>{cat.label}</span>
                            <span className="text-sm text-gray-600 font-normal">({list.length})</span>
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5 ml-7">{cat.explainer}</div>
                        </div>
                        <span className="text-gray-500 ml-2 shrink-0">{isOpen ? "▼" : "▶"}</span>
                      </button>
                      {isOpen && (
                        <div className="border-t border-gray-200 bg-white">
                          {list.slice(0, 30).map((row, idx) => {
                            // iter105 — Détecte les anomalies basées sur un provisoire (avec id de provisoire dans la ligne)
                            // Pour les doublons (ids[]), on peut déverrouiller la 2e occurrence (le doublon)
                            const isProvisoireBased = ["incoherences_alloue_superieur_montant", "ecarts_validation_sans_compte", "provisoires_orphelins"].includes(cat.key);
                            const isDoublon = ["doublons_dedup_key", "doublons_acct_servicer_ref"].includes(cat.key);
                            const isFacture = ["sur_encaissements_factures", "factures_payees_mais_reste"].includes(cat.key);
                            const isValidated = !!row.validee_at;
                            return (
                              <div key={idx} className="px-4 py-2.5 border-b border-gray-100 last:border-0 text-sm" data-testid={`audit-row-${cat.key}-${idx}`}>
                                <div className="text-gray-800">{describeAnomaly(cat.key, row)}</div>
                                {/* iter105 — Libellé complet visible (utile pour l'utilisateur) */}
                                {row.libelle && (
                                  <div className="text-xs text-gray-500 mt-1 italic whitespace-pre-wrap break-words" data-testid={`audit-libelle-${idx}`}>
                                    « {row.libelle} »
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-1.5 mt-1.5">
                                  {/* Lien vers la facture */}
                                  {isFacture && row.id && (
                                    <a
                                      href={`/factures/${row.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-white border border-gray-300 hover:bg-gray-50"
                                      data-testid={`audit-view-facture-${row.id}`}
                                    >
                                      <Eye className="w-3 h-3" /> Voir la facture →
                                    </a>
                                  )}
                                  {/* Déverrouillage via la facture (sub-modal listant les provisoires) */}
                                  {cat.key === "sur_encaissements_factures" && row.id && (
                                    <button
                                      onClick={() => setUnlockTarget({ facture_id: row.id, numero: row.numero })}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-100 border border-amber-300 text-amber-800 hover:bg-amber-200"
                                      data-testid={`audit-unlock-${row.id}`}
                                    >
                                      🔓 Déverrouiller le mouvement CAMT
                                    </button>
                                  )}
                                  {/* iter105 — Déverrouillage DIRECT pour les anomalies basées sur provisoire */}
                                  {isProvisoireBased && row.id && isValidated && (
                                    <DirectUnlockButton pid={row.id} onDone={onReload} />
                                  )}
                                  {/* iter105 — Lien vers l'import pour voir la ligne en contexte */}
                                  {(isProvisoireBased || isDoublon) && row.import_id && (
                                    <a
                                      href={`/comptabilite?tab=import-bancaire&import_id=${row.import_id}&focus_provisoire=${row.id || (row.ids && row.ids[0]) || ""}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-white border border-gray-300 hover:bg-gray-50"
                                      data-testid={`audit-view-import-${idx}`}
                                    >
                                      <Eye className="w-3 h-3" /> Voir dans l&apos;import →
                                    </a>
                                  )}
                                  {/* iter105 — Pour les doublons : déverrouille la 2e occurrence (le vrai doublon) */}
                                  {isDoublon && row.ids && row.ids[1] && (
                                    <DirectUnlockButton
                                      pid={row.ids[1]}
                                      onDone={onReload}
                                      label="🔓 Déverrouiller le doublon"
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {list.length > 30 && (
                            <div className="px-4 py-2 text-xs text-gray-500 italic bg-gray-50">
                              … et {list.length - 30} autres (max 50 retournés par catégorie — affiner par import_id pour voir tout)
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          {onReload && (
            <Button variant="default" onClick={onReload} data-testid="audit-reload">
              <RefreshCw className="w-4 h-4 mr-1" /> Relancer l&apos;audit
            </Button>
          )}
        </DialogFooter>

        {unlockTarget && (
          <UnlockProvisoiresModal
            factureId={unlockTarget.facture_id}
            factureNumero={unlockTarget.numero}
            onClose={() => setUnlockTarget(null)}
            onDone={() => { setUnlockTarget(null); if (onReload) onReload(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// iter104 — Sous-modal : liste les provisoires CAMT touchant une facture
// + bouton "Déverrouiller" pour annuler la validation et corriger l'écart.
function UnlockProvisoiresModal({ factureId, factureNumero, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(null); // provisoire id en cours de déverrouillage

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/journal/provisoires/par-facture/${factureId}`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
      setData(null);
    }
    setLoading(false);
  }, [factureId]);

  useEffect(() => { load(); }, [load]);

  const handleUnlock = async (pid) => {
    if (!window.confirm(
      "Confirmer le déverrouillage de ce mouvement ?\n\nCela va :\n" +
      "• Supprimer les écritures comptables créées\n" +
      "• Retirer l'encaissement lié sur la facture\n" +
      "• Remettre le mouvement en statut « rapproché » (modifiable)\n\n" +
      "Vous pourrez ensuite ré-allouer ou re-valider depuis l'onglet rapprochement."
    )) return;
    setActing(pid);
    try {
      const res = await api.post(`/journal/provisoires/${pid}/devalider`);
      toast.success(
        `Mouvement déverrouillé · ${res.data.ecritures_supprimees} écriture(s) annulée(s) · ${res.data.factures_modifiees.length} facture(s) recalculée(s)`,
        { duration: 6000 },
      );
      await load();
      // Notifie le parent pour rafraîchir l'audit
      if (onDone) setTimeout(onDone, 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du déverrouillage");
    }
    setActing(null);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="unlock-provisoires-modal">
        <DialogHeader>
          <DialogTitle>🔓 Déverrouiller un mouvement CAMT</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : !data ? null : (
          <>
            <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm">
              <div className="font-semibold text-amber-900">Facture {data.facture.numero}</div>
              <div className="text-amber-800 mt-1">
                Montant dû : <b>{fmtChfShort(data.facture.montant_brut)} CHF</b> ·
                Encaissé : <b>{fmtChfShort(data.facture.total_encaisse)} CHF</b>
                {data.facture.excedent > 0 && (
                  <> · Surplus : <b className="text-red-700">{fmtChfShort(data.facture.excedent)} CHF</b></>
                )}
              </div>
            </div>

            <div className="mt-3 text-xs text-gray-600 italic">
              Mouvements CAMT qui ont touché cette facture. Choisis celui à déverrouiller pour pouvoir le corriger ensuite.
            </div>

            <div className="mt-2 space-y-2">
              {(data.provisoires || []).map((p) => (
                <div key={p.id} className="border rounded p-3 text-sm bg-white" data-testid={`unlock-provisoire-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">
                        {fmtDate(p.date_valeur)} — {fmtChfShort(p.montant_transaction)} CHF
                        {p.validee_at ? (
                          <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200">Validé</Badge>
                        ) : (
                          <Badge className="ml-2 bg-amber-100 text-amber-700 border-amber-200">Non validé</Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{(p.libelle || "").slice(0, 200)}</div>
                      <div className="text-xs text-gray-700 mt-1">
                        Alloué sur cette facture : <b>{fmtChfShort(p.montant_alloue_sur_facture)} CHF</b>
                        {p.encaissement_id_lie && (
                          <span className="text-gray-500"> · encaissement #{p.encaissement_id_lie.slice(0, 8)}</span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0">
                      {p.validee_at ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUnlock(p.id)}
                          disabled={acting === p.id}
                          className="bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                          data-testid={`unlock-btn-${p.id}`}
                        >
                          {acting === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "🔓 Déverrouiller"}
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-500 italic">Déjà déverrouillé</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {data.provisoires?.length === 0 && (
                <div className="text-center py-6 text-gray-500 text-sm">
                  Aucun mouvement CAMT n&apos;est lié à cette facture.
                </div>
              )}
            </div>
          </>
        )}

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// iter341 — Modal de rapprochement manuel d'un débit bancaire aux factures fournisseurs.
// Multi-sélection (un virement groupé peut couvrir plusieurs factures) + montants
// modifiables + compte d'écart optionnel pour les frais bancaires / escomptes.
function LinkFournisseurModal({ montant, suggere, onClose, onSubmit }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});   // {ffId: {ff, amount}}
  const [compteEcart, setCompteEcart] = useState("");
  const [plan, setPlan] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/journal/plan-comptes").then(({ data }) => setPlan(data || [])).catch(() => {});
  }, []);

  const fetchFF = useCallback(async (search) => {
    setLoading(true);
    try {
      const { data } = await api.get("/journal/fournisseurs/ouvertes", { params: { q: search, limit: 100 } });
      setItems(data.factures || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de recherche");
    } finally { setLoading(false); }
  }, []);

  // Pré-remplit avec la suggestion automatique si elle existe
  useEffect(() => {
    fetchFF(suggere?.reference_interne || "");
  }, [fetchFF, suggere]);

  useEffect(() => {
    const t = setTimeout(() => fetchFF(q), 300);
    return () => clearTimeout(t);
  }, [q, fetchFF]);

  const toggle = (ff) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[ff.id]) delete next[ff.id];
      else {
        const dejaAlloue = Object.values(next).reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
        const dispo = Math.max(Math.round((montant - dejaAlloue) * 100) / 100, 0);
        next[ff.id] = { ff, amount: String(Math.min(ff.reste_a_payer, dispo).toFixed(2)) };
      }
      return next;
    });
  };

  const rows = Object.values(selected);
  const totalAlloue = Math.round(rows.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0) * 100) / 100;
  const ecart = Math.round((montant - totalAlloue) * 100) / 100;

  const submit = async () => {
    if (rows.length === 0) return;
    setSubmitting(true);
    const body = {
      facture_fournisseur_ids: rows.map((x) => x.ff.id),
      amounts: rows.map((x) => ({ facture_fournisseur_id: x.ff.id, amount: parseFloat(x.amount) || 0 })),
    };
    if (ecart > 0.05 && compteEcart) body.compte_ecart_numero = compteEcart;
    await onSubmit(body);
    setSubmitting(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="link-fournisseur-modal">
        <DialogHeader>
          <DialogTitle>Rapprocher à des factures fournisseurs</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Paiement bancaire de <b className="font-mono">CHF {fmtChf(montant)}</b>. Sélectionnez la ou les factures
            fournisseurs réglées par ce virement. L&apos;écriture 2000 → banque est créée automatiquement et la facture
            passe en « payée » lorsqu&apos;elle est entièrement couverte.
          </div>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Référence, fournisseur, n° de facture ou montant…"
              className="pl-8 h-9"
              data-testid="ff-search-input"
            />
          </div>

          <div className="border border-gray-200 rounded max-h-64 overflow-auto">
            {loading && <div className="text-center py-6 text-gray-400"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>}
            {!loading && items.length === 0 && (
              <div className="text-center py-6 text-sm text-gray-400">Aucune facture fournisseur ouverte</div>
            )}
            {!loading && items.map((ff) => (
              <button
                key={ff.id}
                onClick={() => toggle(ff)}
                className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50 flex items-center gap-2 ${selected[ff.id] ? "bg-amber-50" : ""}`}
                data-testid={`ff-option-${ff.id}`}
              >
                <input type="checkbox" readOnly checked={!!selected[ff.id]} className="pointer-events-none" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm">
                    <code className="font-mono font-semibold">{ff.reference_interne}</code>
                    {ff.numero_facture && <span className="text-xs text-gray-500 ml-1.5">n° {ff.numero_facture}</span>}
                  </div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {ff.fournisseur_nom || "—"} · échéance {fmtDate(ff.date_echeance)}
                  </div>
                </div>
                <div className="text-right text-xs font-mono flex-shrink-0">
                  <div>{fmtChf(ff.montant_ttc)}</div>
                  {ff.deja_paye > 0.05 && <div className="text-[10px] text-amber-700">reste {fmtChf(ff.reste_a_payer)}</div>}
                </div>
              </button>
            ))}
          </div>

          {rows.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs">Montants alloués</Label>
              {rows.map((x) => (
                <div key={x.ff.id} className="flex items-center gap-2">
                  <span className="text-xs flex-1 truncate">
                    <code className="font-mono">{x.ff.reference_interne}</code> · {x.ff.fournisseur_nom || "—"}
                  </span>
                  <Input
                    value={x.amount}
                    onChange={(e) => setSelected((prev) => ({ ...prev, [x.ff.id]: { ...prev[x.ff.id], amount: e.target.value } }))}
                    className="h-8 w-28 text-right font-mono text-xs"
                    data-testid={`ff-amount-${x.ff.id}`}
                  />
                </div>
              ))}
              <div className="text-xs flex items-center justify-between pt-1 border-t border-gray-100">
                <span className="text-gray-600">Total alloué</span>
                <span className="font-mono font-semibold">CHF {fmtChf(totalAlloue)}</span>
              </div>
              {Math.abs(ecart) > 0.05 && (
                <div className={`text-xs ${ecart > 0 ? "text-amber-700" : "text-red-600"}`} data-testid="ff-ecart">
                  {ecart > 0
                    ? `Reste CHF ${fmtChf(ecart)} non alloué — choisissez un compte d'écart (frais bancaires, escompte…) ou ajoutez une facture.`
                    : `Total supérieur au mouvement de CHF ${fmtChf(Math.abs(ecart))} — corrigez les montants.`}
                </div>
              )}
              {ecart > 0.05 && (
                <CompteSelect
                  value={compteEcart}
                  onValueChange={setCompteEcart}
                  options={plan.map((c) => ({ value: c.numero, label: `${c.numero} – ${c.libelle}` }))}
                  placeholder="Compte d'écart…"
                  className="h-8 text-xs w-72"
                  data-testid="ff-compte-ecart"
                />
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ff-cancel-btn">Annuler</Button>
          <Button
            onClick={submit}
            disabled={rows.length === 0 || submitting || ecart < -0.05 || (ecart > 0.05 && !compteEcart)}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="ff-link-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : `Rapprocher ${rows.length || ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
